import { brandAndCountryFromZone } from '@seek/audience-zones';
import type { TrackingProps } from '@seek/tealium-adapter/lib-types/types';
import React, { useCallback } from 'react';

import { getSiteCountry } from 'src/siteConfig';

import { logDevInfo } from '../utils/logDevInfo';

import { trackLink } from './tracking/trackLink';
import { trackView } from './tracking/trackView';
import { useConfig } from './useConfig';
import { type QueryParamTab, useQueryParam } from './useQueryParam';

export interface TrackingData {
  siteSection: string;
  currentPage: string;
  seekAdvertiserId?: string;
  userId?: string;
  adStatus?: QueryParamTab;
  siteSubSection: string;
}

export interface TrackingContextData {
  trackingData: TrackingData;
}

export const defaultTrackingData: TrackingData = {
  siteSection: 'jobs',
  currentPage: 'job list',
  siteSubSection: 'ecaja',
};

export const TrackingContext = React.createContext<TrackingContextData>({
  trackingData: defaultTrackingData,
});

export const useTracking = () => {
  const { environment, site, locale, zone } = useConfig();
  const { tab } = useQueryParam();
  const { trackingData } = React.useContext(TrackingContext);
  const isTrackingEnabled = environment !== 'development';

  const [brand] = brandAndCountryFromZone(zone) || [];

  const trackEvent = useCallback(
    (eventName: string, attributes: TrackingProps = {}) => {
      const trackingProps = {
        ...trackingData,
        ...attributes,
        adStatus: tab,
        brand,
        siteLanguage: locale?.split('-')[0],
        zone,
        isLoggedIn: Boolean(trackingData.userId),
        siteCountry: getSiteCountry(site),
      };

      if (isTrackingEnabled) {
        trackLink(eventName, trackingProps);
      } else {
        logDevInfo(`event: ${eventName}`, trackingProps);
      }
    },
    [brand, isTrackingEnabled, locale, site, tab, trackingData, zone],
  );

  const trackPageView = useCallback(
    (eventName: string, attributes: TrackingProps = {}) => {
      const trackingProps = {
        ...trackingData,
        ...attributes,
        adStatus: tab,
        brand,
        siteLanguage: locale?.split('-')[0],
        zone,
        isLoggedIn: Boolean(trackingData.userId),
        siteCountry: getSiteCountry(site),
      };

      if (isTrackingEnabled) {
        trackView(eventName, trackingProps);
      } else {
        logDevInfo({
          eventName,
          trackingProps,
        });
      }
    },
    [tab, trackingData, isTrackingEnabled, brand, locale, site, zone],
  );

  return {
    trackingData,
    trackEvent,
    trackPageView,
  };
};
